/**
 * Deriving rooms from the wall graph.
 *
 * Rooms are never drawn by hand. The walls form a planar graph and its bounded
 * faces are the rooms, which is the only way "move a wall and watch the areas
 * update" can work without the user re-tracing anything.
 *
 * The traversal is the standard one over half-edges: each wall contributes two
 * directed edges, and the edge following u->v is the one leaving v that sits
 * immediately clockwise from the edge back to u. With y pointing down, that
 * makes every bounded face come out with a positive signed area and the single
 * unbounded face negative, so telling rooms from the outside world is a sign
 * test.
 */

import {
  EPSILON,
  angleOf,
  area,
  insetPolygon,
  interiorPoint,
  mm2ToM2,
  pointInPolygon,
  signedArea,
  subtract,
} from './geometry';
import type { Level, Point, RoomMeta, Wall } from './types';

/** A bounded face of the wall graph: one room's centreline outline. */
export interface Face {
  wallIds: string[];
  junctionIds: string[];
  /** Corner points, following the wall centrelines. */
  polygon: Point[];
  signedArea: number;
}

/** A face joined to whatever metadata claimed it. */
export interface Room {
  /** Stable id: the matched metadata's, else derived from the face. */
  id: string;
  meta: RoomMeta | null;
  face: Face;
  /** Outline inset to the inner wall faces -- what the area is measured from. */
  polygon: Point[];
  areaM2: number;
  /** A point inside the room, for labelling and for re-matching after an edit. */
  seed: Point;
}

interface HalfEdge {
  wallId: string;
  from: string;
  to: string;
}

/** Faces smaller than this are traversal artefacts, not rooms (1 cm²). */
const MIN_FACE_AREA_MM2 = 100;

function halfEdgeKey(edge: HalfEdge): string {
  return `${edge.wallId}|${edge.from}`;
}

/**
 * Builds, for every junction, its incident walls sorted by the angle of the
 * direction leading away from it.
 */
function buildAdjacency(
  level: Level,
  positions: Map<string, Point>,
): Map<string, Array<{ wallId: string; to: string; angle: number }>> {
  const adjacency = new Map<string, Array<{ wallId: string; to: string; angle: number }>>();

  const record = (from: string, to: string, wallId: string) => {
    const origin = positions.get(from);
    const target = positions.get(to);
    if (!origin || !target) return;
    const list = adjacency.get(from) ?? [];
    list.push({ wallId, to, angle: angleOf(subtract(target, origin)) });
    adjacency.set(from, list);
  };

  for (const wall of level.walls) {
    // A wall looping back to its own junction has no meaningful rotation order
    // and would corrupt the traversal.
    if (wall.a === wall.b) continue;
    record(wall.a, wall.b, wall.id);
    record(wall.b, wall.a, wall.id);
  }

  for (const list of adjacency.values()) {
    list.sort((left, right) => left.angle - right.angle);
  }
  return adjacency;
}

/** Every bounded face of the wall graph, largest first. */
export function extractFaces(level: Level): Face[] {
  const positions = new Map<string, Point>(
    level.junctions.map((junction) => [junction.id, { x: junction.x, y: junction.y }]),
  );
  const adjacency = buildAdjacency(level, positions);

  const visited = new Set<string>();
  const faces: Face[] = [];

  const starts: HalfEdge[] = [];
  for (const wall of level.walls) {
    if (wall.a === wall.b) continue;
    if (!positions.has(wall.a) || !positions.has(wall.b)) continue;
    starts.push({ wallId: wall.id, from: wall.a, to: wall.b });
    starts.push({ wallId: wall.id, from: wall.b, to: wall.a });
  }

  for (const start of starts) {
    if (visited.has(halfEdgeKey(start))) continue;

    const cycle: HalfEdge[] = [];
    let current = start;

    // The bound is a safety net: a malformed graph must not hang the editor.
    for (let guard = 0; guard <= starts.length; guard++) {
      if (visited.has(halfEdgeKey(current))) break;
      visited.add(halfEdgeKey(current));
      cycle.push(current);

      const neighbours = adjacency.get(current.to);
      if (!neighbours || neighbours.length === 0) break;

      // Find the edge pointing back the way we came, then step one place
      // clockwise around this junction.
      const backIndex = neighbours.findIndex((n) => n.wallId === current.wallId);
      if (backIndex < 0) break;
      const next = neighbours[(backIndex - 1 + neighbours.length) % neighbours.length];

      current = { wallId: next.wallId, from: current.to, to: next.to };
      if (halfEdgeKey(current) === halfEdgeKey(start)) break;
    }

    if (cycle.length < 3) continue;

    const polygon = cycle
      .map((edge) => positions.get(edge.from))
      .filter((p): p is Point => p !== undefined);
    if (polygon.length < 3) continue;

    const signed = signedArea(polygon);
    // Negative is the unbounded face; near-zero is a dangling wall traced out
    // and back again.
    if (signed <= MIN_FACE_AREA_MM2) continue;

    faces.push({
      wallIds: cycle.map((edge) => edge.wallId),
      junctionIds: cycle.map((edge) => edge.from),
      polygon,
      signedArea: signed,
    });
  }

  return faces.sort((left, right) => right.signedArea - left.signedArea);
}

/**
 * Shrinks a face from wall centrelines to inner wall faces, so its area matches
 * how a floor plan states it.
 */
export function faceInnerPolygon(face: Face, wallsById: Map<string, Wall>): Point[] {
  const insets = face.wallIds.map((wallId) => {
    const wall = wallsById.get(wallId);
    return wall ? wall.thickness / 2 : 0;
  });
  return insetPolygon(face.polygon, insets);
}

/**
 * Rooms for a level: faces, inner outlines, areas, and the metadata each face
 * claims.
 *
 * Matching is by seed point. A room keeps its name through any edit that leaves
 * its seed inside the same face -- moving a wall, adding a window -- and loses
 * it only when the topology genuinely changes, which is the moment a human
 * should be asked what the new space is called.
 */
export function computeRooms(level: Level): Room[] {
  const wallsById = new Map(level.walls.map((wall) => [wall.id, wall]));
  const faces = extractFaces(level);

  const unclaimed = new Map<string, RoomMeta>(level.rooms.map((room) => [room.id, room]));

  return faces.map((face, index) => {
    let claimed: RoomMeta | null = null;
    for (const meta of unclaimed.values()) {
      if (pointInPolygon(meta.seed, face.polygon)) {
        claimed = meta;
        unclaimed.delete(meta.id);
        break;
      }
    }

    const polygon = faceInnerPolygon(face, wallsById);
    return {
      id: claimed?.id ?? `face_${index}_${face.junctionIds.join('-')}`,
      meta: claimed,
      face,
      polygon,
      areaM2: mm2ToM2(area(polygon)),
      seed: claimed?.seed ?? interiorPoint(face.polygon),
    };
  });
}

/** The room whose inner outline contains a point, if any. */
export function roomAt(rooms: Room[], point: Point): Room | null {
  for (const room of rooms) {
    if (pointInPolygon(point, room.polygon)) return room;
  }
  return null;
}

export interface LevelMetrics {
  levelId: string;
  name: string;
  rooms: Array<{ id: string; name: string | null; areaM2: number; declaredAreaM2: number | null }>;
  totalAreaM2: number;
  wallLengthM: number;
  openingCounts: { door: number; window: number; opening: number };
}

export interface PlanMetrics {
  totalAreaM2: number;
  levels: LevelMetrics[];
}

/**
 * Summary numbers stored alongside each revision, so comparing scenarios never
 * needs the server to understand geometry.
 */
export function computeMetrics(levels: Level[]): PlanMetrics {
  const levelMetrics = levels.map((level) => {
    const rooms = computeRooms(level);
    const positions = new Map(level.junctions.map((j) => [j.id, { x: j.x, y: j.y }]));

    let wallLengthMm = 0;
    for (const wall of level.walls) {
      const a = positions.get(wall.a);
      const b = positions.get(wall.b);
      if (a && b) wallLengthMm += Math.hypot(b.x - a.x, b.y - a.y);
    }

    const openingCounts = { door: 0, window: 0, opening: 0 };
    for (const opening of level.openings) openingCounts[opening.type] += 1;

    return {
      levelId: level.id,
      name: level.name,
      rooms: rooms.map((room) => ({
        id: room.id,
        name: room.meta?.name ?? null,
        areaM2: roundTo(room.areaM2, 2),
        declaredAreaM2: room.meta?.declaredAreaM2 ?? null,
      })),
      totalAreaM2: roundTo(
        rooms.reduce((sum, room) => sum + room.areaM2, 0),
        2,
      ),
      wallLengthM: roundTo(wallLengthMm / 1000, 2),
      openingCounts,
    };
  });

  return {
    totalAreaM2: roundTo(
      levelMetrics.reduce((sum, level) => sum + level.totalAreaM2, 0),
      2,
    ),
    levels: levelMetrics,
  };
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export { EPSILON };
